import {
  CompanyRepresentativeInfosFragment,
  IdentityType,
  RepresentativeRole,
  UserInfosFragment,
} from '@blockpulse3/graphql/hooks';
import { formatCountry, formatNationality } from '@blockpulse3/helpers';

import { representativeFormDefaults } from './schema';
import { IRepresentativeForm } from './types';

export function getRepresentatives(
  data: CompanyRepresentativeInfosFragment[] | undefined | null,
): IRepresentativeForm[] {
  if (!data) {
    return [];
  }

  const presidents = data.filter(
    (rep) =>
      rep.role === RepresentativeRole.PRESIDENT || rep.role === RepresentativeRole.COMPANY_MANAGER,
  );

  return presidents.map((president) => {
    const isCompany = president?.representativeIdentity.type === IdentityType.COMPANY;

    const company = president?.representativeIdentity.companyIdentity;
    const individual = president?.representativeIdentity?.individualIdentity;

    const firstName =
      (isCompany ? company?.signer?.individualIdentity?.firstName : individual?.firstName) ||
      representativeFormDefaults.firstName;
    const lastName =
      (isCompany ? company?.signer?.individualIdentity?.lastName : individual?.lastName || '') ||
      representativeFormDefaults.lastName;

    const email =
      (isCompany ? company?.signer?.individualIdentity?.email : individual?.email) ||
      representativeFormDefaults.email;

    const position =
      (isCompany ? company?.signer?.individualRepresentative?.position : '') ||
      representativeFormDefaults.position;

    const _address = isCompany ? company?.address : individual?.address;

    const address: IRepresentativeForm['address'] = {
      line: _address?.line || representativeFormDefaults.address.line,
      city: _address?.city || representativeFormDefaults.address.city,
      postalCode: _address?.postalCode || representativeFormDefaults.address.postalCode,
      country: {
        label: formatCountry(_address?.country || 'FRA'),
        value: _address?.country || representativeFormDefaults.address.country.value,
      },
    };

    return {
      id: president.id,
      /* ** Common ** */
      type: president?.representativeIdentity.type || representativeFormDefaults.type,
      role: president?.role || representativeFormDefaults.role,
      address,
      /* ** Company ** */
      name: company?.name || representativeFormDefaults.name,
      registrationEntity:
        company?.registrationEntity || representativeFormDefaults.registrationEntity,
      registrationNumber:
        company?.registrationNumber || representativeFormDefaults.registrationNumber,
      nominalValue: company?.nominalValue || representativeFormDefaults.nominalValue,
      birthdate: individual?.birthdate || representativeFormDefaults.birthdate,
      birthplace: individual?.birthplace || representativeFormDefaults.birthplace,
      corporateForm: company?.corporateForm || representativeFormDefaults.corporateForm,
      creationDate: company?.creationDate || representativeFormDefaults.creationDate,
      shareCapital: company?.shareCapital || representativeFormDefaults.shareCapital,
      nationality: {
        label: formatNationality(individual?.nationality || 'FRA'),
        value: individual?.nationality || 'FRA',
      },
      country: { label: 'FRA', value: 'FRA' },
      /* ** Individual ** */
      firstName,
      lastName,
      email,
      position,
    };
  });
}

export function getUserRepresentative(user: UserInfosFragment | null): IRepresentativeForm {
  return {
    ...representativeFormDefaults,
    firstName: user?.firstName || representativeFormDefaults.firstName,
    lastName: user?.lastName || representativeFormDefaults.lastName,
    birthdate: user?.individualIdentity?.birthdate || representativeFormDefaults.birthdate,
    birthplace: user?.individualIdentity?.birthplace || representativeFormDefaults.birthplace,
    email: user?.email || representativeFormDefaults.email,
    address: {
      line: user?.individualIdentity?.address?.line || representativeFormDefaults.address.line,
      city: user?.individualIdentity?.address?.city || representativeFormDefaults.address.city,
      postalCode:
        user?.individualIdentity?.address?.postalCode ||
        representativeFormDefaults.address.postalCode,
      country: {
        label: formatCountry(user?.individualIdentity?.address?.country || 'FRA'),
        value: user?.individualIdentity?.address?.country || 'FRA',
      },
    },
  };
}
